/**
 * The ECharts mount point (SPEC §3, §9.4).
 *
 * §3 allows `echarts-for-react` when its peer deps accept React 19, or "the
 * ~40-line wrapper by hand (init, setOption, resize observer, dispose)". This
 * is the hand-written one: it is four small effects, it owns its disposal
 * precisely, and it keeps the chart out of the dependency's own release cycle.
 * `echarts` itself is the pinned library either way.
 *
 * Default-exported and loaded lazily by `VisualHost`, so a report whose only
 * visual is a table never downloads the charting library at all — which is
 * four of the five seed reports.
 *
 * Disposal matters more than it looks. An ECharts instance holds a canvas and
 * a resize listener; leaking one per parameter change on a report someone
 * leaves open all afternoon is a browser tab that slowly dies.
 */

// Imported piecewise from `echarts/core` rather than as `echarts`, which pulls
// every chart type and roughly triples the bundle. Registering only what the
// adapters use keeps the download honest — this portal is replacing a tool
// people already complain is slow. Add the matching imports here when M7 adds
// an adapter; an unregistered chart type fails loudly at setOption.
import { BarChart, HeatmapChart, LineChart, PieChart, ScatterChart } from 'echarts/charts'
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TooltipComponent,
  VisualMapComponent,
} from 'echarts/components'
import * as echarts from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { useEffect, useRef } from 'react'

import type { EChartsOption } from 'echarts'

echarts.use([
  LineChart,
  // `combo` mixes bars and lines; `area` is a filled `LineChart`, so no
  // separate registration. An unregistered type fails at setOption, not build.
  BarChart,
  // Bespoke report panels (custom-reports/sales-insights): donut, bubble and
  // heatmap charts, the visual map that colours a heatmap, and reference lines.
  PieChart,
  ScatterChart,
  HeatmapChart,
  VisualMapComponent,
  MarkLineComponent,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  CanvasRenderer,
])

export interface ChartProps {
  readonly option: EChartsOption
  readonly height?: number
  /** Labels the chart for screen readers; charts are images to them. */
  readonly ariaLabel: string
  /** Called with the clicked point's x value (§9.5). */
  readonly onSelectPoint?: ((value: string | number) => void) | undefined
}

export default function Chart({ option, height = 280, ariaLabel, onSelectPoint }: ChartProps) {
  const container = useRef<HTMLDivElement>(null)
  const chart = useRef<echarts.ECharts | null>(null)

  useEffect(() => {
    if (container.current === null) return
    const instance = echarts.init(container.current, undefined, { renderer: 'canvas' })
    chart.current = instance

    const observer = new ResizeObserver(() => {
      instance.resize()
    })
    observer.observe(container.current)

    return () => {
      observer.disconnect()
      instance.dispose()
      chart.current = null
    }
  }, [])

  useEffect(() => {
    // `notMerge` so a series removed by a parameter change actually leaves the
    // chart rather than lingering from the previous option object.
    chart.current?.setOption(option, { notMerge: true })
  }, [option])

  // Cross-panel selection (§9.5).
  //
  // Deliberately a zrender-level click over the whole plot area, resolved to
  // the nearest category — **not** ECharts' series `click` event. That event
  // fires only when the pointer lands on a mark, which for a line series is a
  // symbol a few pixels across: a target that is hard to hit on a mouse and
  // unusable on a laptop trackpad. The reference implementation in
  // `docs/integration-brief.md` gets a large target for free because its
  // month picker is a column chart; a line chart has to make one.
  //
  // Listeners are cleared before re-registering, so a re-render cannot stack a
  // second handler and drill down twice from one click.
  useEffect(() => {
    const instance = chart.current
    if (instance === null) return undefined
    const zr = instance.getZr()
    zr.off('click')
    if (onSelectPoint === undefined) return undefined

    zr.on('click', (event: { offsetX: number; offsetY: number }) => {
      const at: [number, number] = [event.offsetX, event.offsetY]
      // Ignore clicks on the legend, the axis labels, and the margins.
      if (!instance.containPixel('grid', at)) return

      const converted = instance.convertFromPixel({ seriesIndex: 0 }, at)
      const raw = Array.isArray(converted) ? converted[0] : converted
      if (typeof raw !== 'number' || Number.isNaN(raw)) return
      const category = Math.round(raw)

      // The x value is the category label, which is what the manifest's `x`
      // column holds and what the parameter is bound to.
      const option = instance.getOption() as { xAxis?: { data?: unknown[] }[] }
      const labels = option.xAxis?.[0]?.data
      const value = Array.isArray(labels) ? labels[category] : undefined
      if (typeof value === 'string' || typeof value === 'number') onSelectPoint(value)
    })

    return () => {
      zr.off('click')
    }
  }, [onSelectPoint])

  return (
    <div
      ref={container}
      role="img"
      aria-label={ariaLabel}
      style={{ height }}
      className={onSelectPoint === undefined ? 'w-full' : 'w-full cursor-pointer'}
    />
  )
}
